import {
	AlertDialog,
	AlertDialogBody,
	AlertDialogContent,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogOverlay,
	Box,
	Button,
	Flex,
	Grid,
	HStack,
	List,
	ListItem,
	Text
} from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';

import { useSubscription } from '@rta/rta-blueprint-library';

import { customerState, projectState, selectNavigation, useAppSelector } from '@state';
import { EstimateItemizedContent } from './estimate-itemized-content';
import { type DesignPriceBookResolution, fetchDesignPriceBook } from '../../../external-endpoints/fetch-design-price-book';
import { formatPrice, getEstimatedTax, isRenderingStale } from '../../../helpers';
import { patchPricePin } from '../../../external-endpoints/patch-price-pin';
import { PriceBookChip } from './price-book-chip';
import { RenderingCaptureBox, RtaFeatureIconButton } from '@components';
import { useAppToast, useDesign, usePriceBook } from '@hooks';
import { useKeystoneSession } from '../../../auth/use-keystone-session';
import { useEstimateDownloadFeature, useEstimateLinkFeature } from '@features/document';

export const EstimatePanel = () => {
	const [controller, design] = useDesign();
	const customer = useAppSelector(customerState);
	const project = useAppSelector(projectState);
	const appNav = useAppSelector(selectNavigation);
	const { getToken } = useKeystoneSession();
	const appToast = useAppToast();

	// Re-render the estimate on any appliance/panel/island mutation — not just deal-id
	// changes — so a deleted appliance drops out of pricing immediately. Bound to
	// controller.kitchen so it also re-binds when the active kitchen instance is swapped
	// (draft creation on load, promoteDraft + repoint on save).
	useSubscription(controller?.kitchen ?? null, false, [
		['kitchen_deal_id', 'updated'],
		['*', 'added'],
		['*', 'deleted'],
		['*', 'updated'],
		['*', 'moved']
	]);
	const kitchenData = controller?.kitchen ?? null;

	// The pin is mirrored into React state because Kitchen's setter does not @Publish — without this the
	// catalog swap would not re-run and the totals below would keep the previous version's numbers.
	const [pinnedVersionId, setPinnedVersionId] = useState<string | null>(kitchenData?.priceBookVersionId ?? null);
	const [resolution, setResolution] = useState<DesignPriceBookResolution | null>(null);

	useEffect(() => { setPinnedVersionId(kitchenData?.priceBookVersionId ?? null); }, [kitchenData]);

	// The server owns rule evaluation: deal properties never reach the browser, so an unpinned design on a
	// targeting rule would otherwise price on current here and on current in the design package too.
	useEffect(() => {
		let cancelled = false;
		const blueprint = design?.blueprint ?? null;

		const resolve = async (): Promise<void> => {
			if (!blueprint || !appNav.email) return;

			const [resolved] = await fetchDesignPriceBook({
				email: appNav.email,
				designName: blueprint.submissionId ?? blueprint.id,
				version: kitchenData?.version ?? null,
				getToken
			});

			if (cancelled || !resolved) return;
			setResolution(resolved);

			// Only a design nobody has decided for adopts the rule's answer; a stored provenance — including
			// 'designer' with no version, a deliberate follow-current — outranks it.
			const decided = (kitchenData?.priceBookVersionId ?? null) !== null || (kitchenData?.priceBookPinnedBy ?? null) !== null;
			if (!decided) setPinnedVersionId(resolved.effectiveVersionId);
		};

		resolve();
		return () => { cancelled = true; };
	}, [appNav.email, design, getToken, kitchenData]);

	// appliedVersionId is not read: the hook's state change is what re-renders on a catalog swap,
	// and the live read below then picks the new snapshot up.
	const { appliedVersionId: _appliedVersionId, error: priceBookError } = usePriceBook({
		context: kitchenData?.context ?? null,
		pinnedVersionId
	});

	// itemizedMsrp is a live getter, so read it on every render: the subscription above re-renders on any
	// mutation and appliedVersionId re-renders on a catalog swap. Memoising it froze promotion edits out.
	const itemData = kitchenData?.itemizedMsrp ?? null;

	useEffect(() => { if (priceBookError) appToast.error({ title: priceBookError }); }, [priceBookError]);

	const estimate = useEstimateDownloadFeature();
	const storeEstimate = useEstimateLinkFeature();

	const stale = isRenderingStale({ design });

	const [guardOpen, setGuardOpen] = useState(false);
	const pendingRef = useRef<(() => void) | null>(null);
	const guardCancelRef = useRef<HTMLButtonElement>(null);

	// Gate estimate actions behind a stale-rendering warning that lets the user capture a fresh
	// rendering inline before proceeding — reuse the same box the save/favorite modals use.
	const runGuarded = (action: () => void): void => {
		if (stale) {
			pendingRef.current = action;
			setGuardOpen(true);
		} else {
			action();
		}
	};

	// Its own write, like the deal pairing: a price book choice has to survive the designer closing
	// the tool without saving the design.
	const selectPriceBookVersion = async (priceBookVersionId: string | null): Promise<void> => {
		const blueprint = design?.blueprint ?? null;

		if (!blueprint || !appNav.email) return;

		// Carry the choice on the in-memory kitchen first: the designer is usually on an unsaved draft,
		// and that is the copy the next save serializes.
		if (kitchenData) {
			kitchenData.priceBookVersionId = priceBookVersionId;
			// Recorded even when clearing: 'designer' with no version is a deliberate follow-current, and
			// without it the next resolution reads this as "never pinned" and a rule reclaims the design.
			kitchenData.priceBookPinnedBy = 'designer';
			kitchenData.priceBookRuleId = null;
		}
		setPinnedVersionId(priceBookVersionId);

		// A draft has no version number and is not in the blob yet, so the durable write addresses the
		// newest SAVED version — getLatestVersion sorts a draft last. With nothing saved there is nothing
		// to write, and the line above is the whole story until the first save.
		const version = kitchenData?.version ?? blueprint.kitchens?.getLatestVersion()?.version ?? null;

		if (version === null) return;

		const [, error] = await patchPricePin({
			email: appNav.email,
			designName: blueprint.submissionId ?? blueprint.id,
			version,
			priceBookVersionId,
			getToken
		});

		if (error) appToast.error({ title: error });
	};

	const estimateGuarded = { ...estimate, handler: () => runGuarded(estimate.handler) };
	const storeEstimateGuarded = { ...storeEstimate, handler: () => runGuarded(storeEstimate.handler) };

	if (!kitchenData) return null;

	const subtotal = itemData?.subtotal ?? 0;

	const { tax: estimatedTax, zipcode, state: taxState } = getEstimatedTax({ kitchen: kitchenData, customer, project });
	const total = subtotal + estimatedTax;

	return (
		<Box width="100%" height="100%" overflowY="hidden">
			<Grid width="100%" height="100%" templateRows="auto 1fr auto 95px">
				<HStack h="35px" w="full" background="blue.400" px={6} py={2} justifyContent="space-between" mb={2}>
					<Text textAlign="center" color="white" fontSize={18} position="absolute">ITEMIZED ESTIMATE</Text>
					<RtaFeatureIconButton feature={storeEstimateGuarded} iconProps={{ fontSize: '0.75em' }} alignContent="end" position="absolute" top="5px" right="50px"/>
					<RtaFeatureIconButton feature={estimateGuarded} iconProps={{ fontSize: '0.75em' }} alignContent="end" position="absolute" top="5px" right="10px"/>
				</HStack>
				<EstimateItemizedContent kitchenData={kitchenData} itemData={itemData}/>
				<PriceBookChip kitchen={kitchenData} readonly={controller?.readonly ?? true} pinnedVersionId={pinnedVersionId} candidateVersionId={resolution?.candidateVersionId ?? null} onSelectVersion={selectPriceBookVersion}/>
				<Box pl="10px" pr="10px" pt=".5em" mt="5px" borderTop="1px">
					<List spacing={2} fontSize={14}>
						<ListItem key="subtotal">
							<Flex justifyContent="space-between">
								<Text>SUBTOTAL</Text>
								<Text>{formatPrice(subtotal)}</Text>
							</Flex>
						</ListItem>
						<ListItem key="salestax">
							<Flex justifyContent="space-between">
								<Text>TAX{taxState ? ` ${taxState}` : ''} (estimated)</Text>
								<Text>{zipcode ? formatPrice(estimatedTax) : '---.--'}</Text>
							</Flex>
						</ListItem>
						<ListItem key="total">
							<Flex justifyContent="space-between">
								<Text>TOTAL</Text>
								<Text>{formatPrice(total)}</Text>
							</Flex>
						</ListItem>
					</List>
				</Box>
			</Grid>
			<AlertDialog
				isOpen={guardOpen}
				leastDestructiveRef={guardCancelRef}
				onClose={() => setGuardOpen(false)}
			>
				<AlertDialogOverlay>
					<AlertDialogContent>
						<AlertDialogHeader fontSize="lg" fontWeight="bold">
							Rendering may be out of date
						</AlertDialogHeader>
						<AlertDialogBody>
							<Text fontSize="sm">
								This design changed after its rendering was captured, or has no rendering set. The estimate image may not match the current kitchen.
								Capture a fresh rendering below, or continue anyway.
							</Text>
							{design?.tdController && (
								<Box mt={3}>
									<RenderingCaptureBox
										tdController={design.tdController}
										active={guardOpen}
									/>
								</Box>
							)}
						</AlertDialogBody>
						<AlertDialogFooter gap={2}>
							<Button ref={guardCancelRef} onClick={() => setGuardOpen(false)}>
								Cancel
							</Button>
							<Button
								colorScheme="blue"
								onClick={() => {
									setGuardOpen(false);
									pendingRef.current?.();
								}}
							>
								Continue Anyway
							</Button>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialogOverlay>
			</AlertDialog>
		</Box>
	);
};
